import {
  QueryObserverResult,
  RefetchOptions,
  UseMutateAsyncFunction,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  useFacebookLogin,
  useVote,
  UseVoteAPIResponseDTO,
  useWidget,
  VoteAPIResponseCode,
} from '@telescope/cassini-hooks';
import { useStorage } from 'providers';
import React, { useCallback, useMemo } from 'react';
import { ErrorType, SnapshotType } from 'types';
import { getEmailLoginPayload, getFBLoginPayload } from '../lib';
import { AuthMethod, AuthType, AUTH_LOCALSTORAGE_KEY } from '../utils/constants';
import { v4 as uuidv4 } from 'uuid';
import { Open } from 'generated-types/widget';

// TODO: Replace 'any' with correct typings for auth function payloads
export interface AuthProviderConfig<Error = unknown> {
  key?: string;
  waitInitial?: boolean;
  LoaderComponent?: () => JSX.Element;
  ErrorComponent?: ({ error }: { error: Error | null }) => JSX.Element;
}

export interface AuthContextValue<
  User = unknown,
  Error = unknown,
  LoginCredentials = unknown,
  RegisterCredentials = any
> {
  user: User | undefined;
  loginWithEmail: UseMutateAsyncFunction<User, any, LoginCredentials>;
  loginWithFacebook: UseMutateAsyncFunction<User, any, LoginCredentials>;
  logout: UseMutateAsyncFunction<any, any, void, any>;
  registerWithEmail: UseMutateAsyncFunction<User, any, RegisterCredentials>;
  registerWithFacebook: UseMutateAsyncFunction<User, any, RegisterCredentials>;
  updateUserVoteHistory: UseMutateAsyncFunction<User, any, string>;
  isLoggingIn: boolean;
  isLoggingOut: boolean;
  isRegistering: boolean;
  refetchUser: (options?: RefetchOptions | undefined) => Promise<QueryObserverResult<User, Error>>;
  error: Error | null;
  authMethods: Record<AuthMethod, boolean>;
  isMethodEnabled: (method: AuthMethod) => boolean;
  hasRegistration: boolean;
}

export interface AuthProviderProps {
  children: React.ReactNode;
}

export function initReactQueryAuth<
  User = unknown,
  Error = unknown,
  LoginCredentials = unknown,
  RegisterCredentials = unknown
>(config: AuthProviderConfig<Error>) {
  const AuthContext = React.createContext<AuthContextValue<User, Error, LoginCredentials, RegisterCredentials> | null>(
    null
  );
  AuthContext.displayName = 'AuthContext';

  const {
    key = 'auth-user',
    waitInitial = true,
    LoaderComponent = () => <div>Loading...</div>,
    ErrorComponent = (error: any) => <div style={{ color: 'tomato' }}>{JSON.stringify(error, null, 2)}</div>,
  } = config;

  function AuthProvider({ children }: AuthProviderProps): JSX.Element {
    const storage = useStorage();
    const loginTelescope = useVote();
    const { login: loginFB } = useFacebookLogin(
      (e) => console.log('FB Login success', e),
      () => console.log('Logout'),
      () => console.log('FB Error')
    );
    // const authenticateVoteApi = useVote();
    const queryClient = useQueryClient();
    const {
      data: { sid, snapshot },
    } = useWidget<Open>({ select: (data) => data });
    const {
      data: { hasRegistration },
    } = useWidget<Open>({ select: (data) => data.widget.settings.vote_settings.ruleset_parameters });
    const { data: authSettings } = useWidget<Open>({ select: (data) => data.widget.globalViews.auth.settings });
    const authMethods = useMemo(
      () => ({
        [AuthMethod.FACEBOOK]: !!(authSettings.isFacebookLoginEnabled && authSettings.fbAppId),
        [AuthMethod.EMAIL]: authSettings.isEmailLoginEnabled,
        [AuthMethod.OAUTH]: !!authSettings.hasOIDC,
        [AuthMethod.ANONYMOUS]: !authSettings.isAuthenticationEnabled,
      }),
      [
        authSettings.fbAppId,
        authSettings.hasOIDC,
        authSettings.isEmailLoginEnabled,
        authSettings.isFacebookLoginEnabled,
        authSettings.isAuthenticationEnabled,
      ]
    );

    const loadUser = async (): Promise<User> => {
      const storedUser = storage.getItem(AUTH_LOCALSTORAGE_KEY);

      // Refetch the user's vote history if their SID doesn't match the currently open SID
      if (storedUser && storedUser.sid !== sid && snapshot.type === SnapshotType.OPEN) {
        const { user_id, method } = storedUser.user;
        const response = await loginTelescope({ user_id, method, action_type: 'get' });
        const { votestring } = response as any;
        const votestringObj = votestring ? JSON.parse(votestring) : { total: 0 };
        const updatedUser = { user: storedUser.user, sid, votestring: votestringObj };
        storage.setItem(AUTH_LOCALSTORAGE_KEY, updatedUser);
        return updatedUser as User;
      }

      // otherwise, just return the user from localstorage.
      return storedUser;
    };

    const logoutFn = async () => {
      return storage.removeItem(AUTH_LOCALSTORAGE_KEY);
    };

    const updateVoteString = async (votestring: string) => {
      return { ...user, votestring } as User;
    };

    const loginWithEmail = async (payload: any) => {
      const emailLoginPayload = getEmailLoginPayload(payload);
      const { response_code, votestring } = (await loginTelescope({
        ...emailLoginPayload,
        action_type: AuthType.LOGIN,
      })) as UseVoteAPIResponseDTO;

      if (response_code !== VoteAPIResponseCode.VALID) {
        return Promise.reject(response_code);
      }
      const userData = { user: emailLoginPayload, sid, votestring };
      return userData as User;
    };

    const registerWithEmail = async ({
      payload,
      checkIfUserAlreadyRegistered,
    }: {
      payload: any;
      checkIfUserAlreadyRegistered: boolean;
    }) => {
      const emailLoginPayload = getEmailLoginPayload(payload);

      // Step 1 in registration is to check if the user already exists. We don't want to save the user in case the registration has additional fields, so already reject the promise
      if (checkIfUserAlreadyRegistered) {
        const { response_code } = (await loginTelescope({
          ...emailLoginPayload,
          action_type: AuthType.LOGIN,
        })) as UseVoteAPIResponseDTO;

        const isUserAlreadyRegistered = response_code === VoteAPIResponseCode.VALID;
        return { user: emailLoginPayload, sid, isUserAlreadyRegistered } as User;
      }

      // Step 2 - Register the user. This will include any additional required form fields
      const { response_code, votestring } = (await loginTelescope({
        ...emailLoginPayload,
        action_type: AuthType.REGISTER,
      })) as UseVoteAPIResponseDTO;

      if (response_code !== VoteAPIResponseCode.VALID) {
        return Promise.reject(response_code);
      }

      const userData = { user: emailLoginPayload, sid, votestring };

      return userData as User;
    };

    const loginWithFacebook = async (payload: any) => {
      const { user: facebookUser } = await loginFB(window.location.pathname, window.location.pathname, {
        ...(authSettings.requestEmailScope && { scope: 'email' }),
      });

      if (!facebookUser) {
        return Promise.reject(new Error(ErrorType.FB_GENERIC));
      }

      if (authSettings.requestEmailScope && !facebookUser.email) {
        return Promise.reject(new Error(ErrorType.FB_PERMISSIONS));
      }
      const fbLoginPayload = getFBLoginPayload(payload, facebookUser);

      const { response_code, votestring } = (await loginTelescope({
        ...fbLoginPayload,
        action_type: AuthType.LOGIN,
      })) as UseVoteAPIResponseDTO;

      if (response_code !== VoteAPIResponseCode.VALID) {
        return Promise.reject(new Error(response_code));
      }

      const userData = { user: fbLoginPayload, sid, votestring };

      return userData as User;
    };

    const registerWithFacebook = async ({
      payload,
      checkIfUserAlreadyRegistered,
    }: {
      payload: any;
      checkIfUserAlreadyRegistered: boolean;
    }) => {
      const { user: facebookUser } = await loginFB(window.location.pathname, window.location.pathname, {
        ...(authSettings.requestEmailScope && { scope: 'email' }),
      });

      if (!facebookUser) {
        return Promise.reject(new Error(ErrorType.FB_GENERIC));
      }

      if (authSettings.requestEmailScope && !facebookUser.email) {
        return Promise.reject(new Error(ErrorType.FB_PERMISSIONS));
      }
      const fbLoginPayload = getFBLoginPayload(payload, facebookUser);

      // Step 1 in registration is to check if the user already exists.
      if (checkIfUserAlreadyRegistered) {
        const { response_code } = (await loginTelescope({
          ...fbLoginPayload,
          action_type: AuthType.LOGIN,
        })) as UseVoteAPIResponseDTO;

        const isUserAlreadyRegistered = response_code === VoteAPIResponseCode.VALID;
        return { user: fbLoginPayload, sid, isUserAlreadyRegistered } as User;
      }

      // Step 2 - Register the user. This will include any additional required form fields
      const { response_code, votestring } = (await loginTelescope({
        ...fbLoginPayload,
        action_type: AuthType.REGISTER,
      })) as UseVoteAPIResponseDTO;

      if (response_code !== VoteAPIResponseCode.VALID) {
        return Promise.reject(response_code);
      }

      const userData = { user: fbLoginPayload, sid, votestring };

      return userData as User;
    };

    // Create an anonymous user with a random uuid. This is used if the campaign is configured to allow voting without logging in.
    const createAnonymousUser = (): User => {
      const anonymousUser = {
        user: {
          user_id: uuidv4(),
          method: AuthMethod.ANONYMOUS,
        },
        sid,
      };

      return anonymousUser as User;
    };

    const isMethodEnabled = useCallback(
      (method: AuthMethod): boolean => {
        return !!authMethods[method];
      },
      [authMethods]
    );

    const setUser = React.useCallback(
      (data: User) => {
        const { votestring, ...rest } = data as any;
        const votestringObj = votestring ? JSON.parse(votestring) : { total: 0 };
        const user = { ...rest, sid, votestring: votestringObj };
        queryClient.setQueryData([key, { sid }], user);
        storage.setItem(AUTH_LOCALSTORAGE_KEY, user);
        return user;
      },
      [queryClient, storage, sid]
    );

    const {
      data: user,
      error,
      status,
      isLoading,
      isSuccess,
      refetch,
    } = useQuery<User, Error>({
      queryKey: [key, { sid }],
      queryFn: loadUser,
      refetchOnWindowFocus: false,
      initialData: () => {
        const storedUser = storage.getItem(AUTH_LOCALSTORAGE_KEY);
        if (storedUser) {
          return storedUser;
        }

        if (isMethodEnabled(AuthMethod.ANONYMOUS)) {
          const anonymousUser = createAnonymousUser();
          setUser(anonymousUser);
          return anonymousUser;
        }
      },
    });

    const loginWithEmailMutation = useMutation({
      mutationFn: loginWithEmail,
      onSuccess: (user) => {
        setUser(user);
      },
    });

    const loginWithFBMutation = useMutation({
      mutationFn: loginWithFacebook,
      onSuccess: (user) => {
        setUser(user);
      },
    });

    const registerWithEmailMutation = useMutation({
      mutationFn: registerWithEmail,
      onSuccess: (user: any) => {
        // If isUserAlreadyRegistered exists on the object, then we're just checking if the user exists before continuing to the next step, so we don't save the user yet.
        if (user.isUserAlreadyRegistered !== undefined) return;

        setUser(user);
      },
    });

    const registerWithFacebookMutation = useMutation({
      mutationFn: registerWithFacebook,
      onSuccess: (user: any) => {
        // If isUserAlreadyRegistered exists on the object, then we're just checking if the user exists before continuing to the next step, so we don't save the user yet.
        if (user.isUserAlreadyRegistered !== undefined) return;

        setUser(user);
      },
    });

    const logoutMutation = useMutation({
      mutationFn: logoutFn,
      onSuccess: () => {
        queryClient.resetQueries([key]);
      },
    });

    const updateUserMutation = useMutation({
      mutationFn: updateVoteString,
      onSuccess: (user) => {
        setUser(user);
      },
    });

    const value = React.useMemo(
      () => ({
        user,
        error,
        refetchUser: refetch,
        loginWithEmail: loginWithEmailMutation.mutateAsync,
        loginWithFacebook: loginWithFBMutation.mutateAsync,
        isLoggingIn: loginWithEmailMutation.isLoading || loginWithFBMutation.isLoading,
        logout: logoutMutation.mutateAsync,
        isLoggingOut: logoutMutation.isLoading,
        registerWithEmail: registerWithEmailMutation.mutateAsync,
        registerWithFacebook: registerWithFacebookMutation.mutateAsync,
        updateUserVoteHistory: updateUserMutation.mutate,
        isRegistering: registerWithEmailMutation.isLoading || registerWithFacebookMutation.isLoading,
        isMethodEnabled,
        authMethods,
        hasRegistration,
      }),
      [
        user,
        error,
        refetch,
        loginWithEmailMutation.mutateAsync,
        loginWithEmailMutation.isLoading,
        loginWithFBMutation.mutateAsync,
        loginWithFBMutation.isLoading,
        logoutMutation.mutateAsync,
        logoutMutation.isLoading,
        registerWithEmailMutation.mutateAsync,
        registerWithEmailMutation.isLoading,
        registerWithFacebookMutation.mutateAsync,
        registerWithFacebookMutation.isLoading,
        updateUserMutation.mutate,
        isMethodEnabled,
        authMethods,
        hasRegistration,
      ]
    );

    if (isSuccess || !waitInitial) {
      return <AuthContext.Provider value={value as any}>{children}</AuthContext.Provider>;
    }

    if (isLoading) {
      return <LoaderComponent />;
    }

    if (error) {
      return <ErrorComponent error={error} />;
    }

    return <div>Unhandled status: {status}</div>;
  }

  function useAuth() {
    const context = React.useContext(AuthContext);
    if (!context) {
      throw new Error(`useAuth must be used within an AuthProvider`);
    }
    return context;
  }

  return { AuthProvider, AuthConsumer: AuthContext.Consumer, useAuth };
}
